<template>
  <li class="p-5 space-y-2">
    <div class="flex justify-between">
      <span class="flex items-center cursor-pointer" @click="wantsToShow = !wantsToShow">
        <h2 class="capitalize text-lg font-semibold ske">
          {{ roleDisplay }}
        </h2>
        <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 duration-75 text-gray-600 mt-1 ml-2" :class="showing && 'rotate-90'">
          <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
        </svg>
      </span>

      <button v-if="canOverwrite && hasOverwritten" class="text-sm p-0.5 px-2 rounded-lg border border-gray-200 hover:bg-gray-50" @click="removeContainerOverwrite">
        Remove overwrite
      </button>
    </div>
    <transition
      @enter="enter"
      @leave="leave"

      @before-enter="transitioning = true"
      @before-leave="transitioning = true"
      @after-leave="transitioning = false"
      @after-enter="transitioning = false"
    >
      <div
        v-if="showing || isTeleportingConfigurationToSidebar"

        class="duration-75"
        :class="transitioning && 'overflow-hidden'"
      >
        <ul v-if="loading" class="divide-y divide-gray-100 rounded-lg border border-gray-200" role="list">
          <BlockItem :loading="true">
            <span class="ske">Item Item Item 1</span>
          </BlockItem>
          <BlockItem :loading="true">
            <span class="ske">Item Item Item 1 ashtasht</span>
          </BlockItem>
          <BlockItem :loading="true">
            <span class="ske">Item Item Item 1t ths</span>
          </BlockItem>
        </ul>
        <ul v-else
          class="divide-y divide-gray-100 rounded-lg border-t border-gray-200 relative"
          :class="!blocks.length && '!border-0 !divide-transparent'"

          ref="content"
          role="list"
        >
          <BlockItem v-for="(item, index) in blocks"
            class="border-l border-r !border-l-gray-200 !border-r-gray-200 drag-handle block"
            :class="index === 0 && 'rounded-t-lg'"
            v-bind="item"

            :data-id="item.id"
            :key="item.id"
          >
            <span>{{ item.title }}</span>
          </BlockItem>
          <BlockAdder class="ignore-element" :empty="!blocks.length" :role='role' :packageFilter="packageFilter"/>

          <transition
            enter-class="mt-2 opacity-0"
            leave-class="mt-0 opacity-100"
            enter-to-class="mt-0 opacity-100"
            leave-to-class="mt-2 opacity-0"
          >
            <div v-if="canOverwrite && !hasOverwritten" class="absolute w-full h-full duration-200 !border-0 !m-0 top-0 left-0 ignore-element z-20">
              <div class="relative w-full h-full flex justify-center items-center">
                <div class="absolute w-full h-full bg-gray-600 rounded-lg opacity-10"></div>
                <div class="shadow-lg rounded-full max-w-xs p-2 bg-white cursor-pointer hover:bg-gray-50 border border-gray-300 text-gray-700 text-sm relative flex items-center space-x-1" @click="overwriteContainer">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  <span>Override <span class="capitalize font-semibold text-gray-800">{{ overwrittenTargetLabel }}</span>'s container</span>
                </div>
              </div>
            </div>
          </transition>
        </ul>
      </div>
    </transition>
  </li>
</template>

<script>

import BlockItem from './BlockItem.vue'
import BlockAdder from "./BlockAdder.vue"

import Sortable from "sortablejs"
import TransitionFunctionConstructor from "@/mixins/transitionFunctionConstructor"

import { mapGetters } from 'vuex'

export default {
  components: {
    BlockItem,
    BlockAdder,
  },
  mixins: [TransitionFunctionConstructor("enter", "leave", "height")],
  props: ['role', 'loading', 'packageFilter'],
  data() {
    return {
      wantsToShow: false,
      transitioning: true,
    }
  },
  mounted() {
    this.readyToMountSortable && this.mount()
  },
  beforeDestroy() {
    this.destroy()
  },
  methods: {
    destroy() {
      this.instance && this.instance.destroy()
      this.instance = null
    },
    mount() {
      this.destroy()
      requestAnimationFrame(() => {
        this.instance = new Sortable(this.$refs.content, {
          handle: ".drag-handle",
          draggable: ".block",
          sort: true,
          filter: ".ignore-element",
          animation: 150,

          direction: "horizontal",

          onUpdate: () => {
            this.$nextTick(() => {
              const ids = [...this.$refs.content.children].map(el => el.dataset.id)

              this.$store.commit("editor/config", {
                blocks_collection: {
                  [this.page]: {
                    [this.role]: ids.map(id => this.blocks.find(block => block.id === id)).filter(block => block)
                  },
                },
              })
            })
          },
        })
      })
    },
    overwriteContainer() {
      this.$store.commit('editor/config', {
        blocks_collection: {
          [this.page]: {
            [this.role]: [],
          },
        },
      })
    },
    removeContainerOverwrite() {
      this.$store.dispatch('editor/removeConfigKeys', [`blocks_collection.${this.page}.${this.role}`])
    },
  },
  computed: {
    ...mapGetters({
      page: 'editor/page',
      blocksCollection: 'editor/blocksCollection',
      containers: 'editor/pageContainers',
      config: 'editor/config',
      blockHover: 'editor/blockHover',
      lastHover: 'editor/lastHover',
      currentPage: 'editor/currentPage',
      sources: 'editor/sources',
      overrides: 'editor/overrides',
      isTeleportingConfigurationToSidebar: 'editor/isTeleportingConfigurationToSidebar',
    }),
    hoveringOverChildBlocks() {
      const blockHover = this.blockHover
      return this.blocks.some(block => block.id === blockHover)
    },
    isLastHoverInThisContainer() {
      const lastHover = this.lastHover
      return this.blocks.some(block => block.id === lastHover)
    },
    showing() {
      return this.loading || this.wantsToShow || this.hoveringOverChildBlocks || this.isLastHoverInThisContainer
    },
    fullOverrides() {
      return [{
        id: "default",
        name: "Landing page",
      }, ...this.overrides || []]
    },

    overwrittenTargetLabel() {
      const overrides = this.fullOverrides

      let i = 0
      for (const source of this.sources) {
        if (source && source[this.role]) return overrides[i].name
        i++
      }
    },
    canOverwrite() {
      if (this.page === 'default') return false
      const collection = this.blocksCollection
      if (!collection) return false

      const overrides = this.fullOverrides
      let i = 0
      for (const source of this.sources) {
        if (source && source[this.role] && overrides[i].id !== this.page) return true
        i++
      }
    },
    hasOverwritten() {
      if (!this.canOverwrite) return false
      if (!this.blocksCollection[this.page]) return false
      return this.blocksCollection[this.page][this.role]
    },


    roleDisplay() {
      return this.role.replace(/-/g, ' ')
    },
    blocks() {
      if (!this.containers) return []
      return this.containers[this.role] || []
    },
    readyToMountSortable() {
      return this.blocks.length && !this.loading && this.showing
    },
  },
  watch: {
    readyToMountSortable(ready) {
      if (ready) {
        this.mount()
      } else {
        this.destroy()
      }
    },
  },
}

</script>
